// ─────────────────────────────────────────────────────────────────────────────
// INSTRUMENT: Situational Leadership Assessment
// Framework:  Hersey-Blanchard (4 styles × 4 dev levels)
// Items:      16 Situational Judgment items
// Output:     S1/S2/S3/S4 scores, dominant style, flexibility index
// ─────────────────────────────────────────────────────────────────────────────
import { QUESTIONS, QUESTIONS_PER_PAGE, TOTAL_PAGES } from "./questions.js";
import { STYLES, STYLE_MAP, DEV_LEVELS, DEV_MAP, NORMS } from "./factors.js";
import { scoreSL } from "./scoring.js";

function buildAIPrompt(assessment) {
  const sc = assessment.scores || {};
  const ct = assessment.counts || {};
  const dominant  = assessment.dominant  || "—";
  const secondary = assessment.secondary || "—";
  const flex      = assessment.flexibility ?? "—";

  const styleLines = STYLES.map(s => {
    const pct = sc[s.id] != null ? Math.round(sc[s.id] * 10) : 0;
    return `  • ${s.id} ${s.name}: ${ct[s.id] || 0}/16 responses (${pct}%) — best matched to ${s.match} followers`;
  }).join("\n");

  return `You are an expert leadership coach interpreting a Situational Leadership Assessment based on the Hersey-Blanchard model.

DEVELOPMENT REPORT — identify the leader's default style, their style range, flexibility index, and give specific coaching recommendations for adapting to each follower development level.

INSTRUMENT: Situational Leadership Assessment (16 Situational Judgment items, Hersey-Blanchard framework)
CANDIDATE: ${assessment.name} | ${assessment.role || ""} | ${assessment.demo?.company || ""}

STYLE SCORES (out of 16 responses):
${styleLines}

DOMINANT STYLE:  ${dominant}
SECONDARY STYLE: ${secondary}
FLEXIBILITY INDEX: ${flex}/10

STYLE DESCRIPTIONS:
S1 Directing — High task, Low relationship. Use with D1 followers (new, low competence, high enthusiasm).
S2 Coaching  — High task, High relationship. Use with D2 followers (some skill, low motivation/confidence).
S3 Supporting — Low task, High relationship. Use with D3 followers (high skill, variable confidence).
S4 Delegating — Low task, Low relationship. Use with D4 followers (expert, self-motivated, autonomous).

NOTE: The goal is flexible application across all four styles — not a high score on any one. A flexibility index above 7 indicates healthy range; below 4 indicates a strong default that may be misapplied.`;
}

const SL = {
  id:          "sl",
  name:        "Situational Leadership Assessment",
  shortName:   "SL",
  version:     "1.0",
  publisher:   "AXIS360 — Hersey-Blanchard Framework",
  description: "16-item Situational Judgment Assessment measuring leadership style flexibility across the Hersey-Blanchard model. Identifies dominant style (S1–S4), style range, and flexibility index — with a full training kit for individual and group development.",
  icon:        "🎯",
  color:       "#7c3aed",
  bg:          "#f5f3ff",

  totalQuestions: 16,
  timeEstimate:   "8–12 min",
  scaleType:      "sjt-4choice",
  pagesOf:        QUESTIONS_PER_PAGE,
  totalPages:     TOTAL_PAGES,

  questions:   QUESTIONS,
  scales:      STYLES,
  scaleMap:    STYLE_MAP,
  domains:     STYLES,
  devLevels:   DEV_LEVELS,
  devMap:      DEV_MAP,
  norms:       NORMS,

  nineboxAxes: {
    x: { label: "Task Behaviour",         description: "Directive — S1+S2", scaleIds: ["S1","S2"] },
    y: { label: "Relationship Behaviour", description: "Supportive — S2+S3", scaleIds: ["S2","S3"] },
  },
  nineboxCells: null,
  competencies: null,

  score:         scoreSL,
  buildAIPrompt,

  answerFormat: {
    type: "sjt-4choice",
    options: null,
  },
};

export default SL;
